import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import { alpha, useTheme } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';

import { bgGradient } from '../../theme/css';
import Iconify from '../../theme/iconify/iconify';

import Language from '../util/Language';
import Notification from '../util/Notification';
import ForgotPassword from './modal/ForgotPassword';
import { mislecAPI } from '../../util/axios';

function Login({ onLogin, expired, setExpired }) {
    const [token, setToken] = React.useState(sessionStorage.getItem('mISLec_access_token') || null);
    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [forgotPasswordModal, setForgotPasswordModal] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const theme = useTheme();
    const location = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (token) navigate('/');
    }, [token]);

    React.useEffect(() => {
        if (!expired) return;
        setMessage(t('api.auth.verify.401'));
        setSeverity('error');
        setOpen(true);
        setExpired(false);
    }, [expired]);

    const login = () => {
        //  Timestamp
        const timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

        //  Request body
        const data = {
            username: username,
            password: password,
            last_login: timestamp
        };

        //  Send data to server
        mislecAPI.post(`/api/auth/login`, data)
            .then(res => {
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                sessionStorage.setItem('mISLec_access_token', res.data.access_token);

                mislecAPI.get(`/api/users/${res.data.user._id}`)
                    .then(res => {
                        setTimeout(() => {
                            onLogin(res.data);
                            let url = '/';
                            if (location.state && location.state.url && location.state.url !== '/register') url = location.state.url;
                            navigate(url);
                            setOpen(false);
                        }, 2500);
                    })
                    .catch(error => {
                        setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                        setSeverity('error');
                        setOpen(true);
                        setTimeout(() => {
                            setOpen(false);
                        }, 2500);
                    });
            })
            .catch(error => {
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
                setTimeout(() => {
                    setOpen(false);
                }, 2500);
            });
    }

    const handleKeyDown = (event) => {
        if (event.target.type === 'button') return;

        if (event.key === 'Enter') {
            event.preventDefault();
            login();
        }
    }

    const renderForm = (
        <>
            <Grid container>
                <Grid item xs={12} mb={3}>
                    <TextField
                        id='mISLec_username'
                        name='username'
                        label={t('login.inputs.username.label')}
                        value={username}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id='mISLec_password'
                        name='password'
                        label={t('login.inputs.password.label')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type={showPassword ? 'text' : 'password'}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end' sx={{ mr: 1 }}>
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge='end' sx={{ borderRadius: '8px' }}>
                                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography
                        variant='body2'
                        sx={{
                            mt: 1,
                            textAlign: 'right',
                            color: 'primary.main',
                            fontWeight: '600',
                            textDecoration: 'none !important',
                            cursor: 'pointer',
                        }}
                        onClick={() => setForgotPasswordModal(true)}
                    >
                        {t('login.links.forgot_password')}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Stack sx={{ my: 3 }}>
                        <LoadingButton
                            fullWidth
                            size='large'
                            type='submit'
                            variant='contained'
                            color='inherit'
                            onClick={() => login()}
                        >
                            {t('login.buttons.login')}
                        </LoadingButton>
                    </Stack>
                </Grid>
            </Grid>
        </>
    );

    return (
        <>
            <Box
                sx={{
                    ...bgGradient({
                        color: alpha(theme.palette.background.default, 0.9),
                        imgUrl: '/assets/background/overlay_4.jpg',
                    }),
                    height: '100vh',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: 0, md: 16 },
                        left: 0,
                        right: 0,
                        pt: { xs: 1, md: 0 },
                        px: { xs: 1, md: 2 },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Tooltip title={t('header.home')} arrow>
                        <Button
                            variant=''
                            onClick={() => navigate('/')}
                            sx={{ p: 0.5, minWidth: 'auto' }}
                        >
                            <img
                                src='/assets/images/mISLec-logo.png'
                                alt={t('common.logo_alt')}
                                style={{ width: '56px', height: 'auto', maxWidth: '56px' }}
                            />
                        </Button>
                    </Tooltip>
                    <Language />
                </Box>
                <Stack alignItems='center' justifyContent='center' sx={{ height: 1 }}>
                    <Card
                        sx={{
                            p: 5,
                            width: 1,
                            maxWidth: {
                                xs: '95%',
                                md: 420
                            }
                        }}
                    >
                        <Typography variant='h4'>{t('login.title')}</Typography>
                        <Typography variant='body2' sx={{ mt: 2, mb: 5 }}>
                            {t('login.description')}
                            <Link to='/register' style={{ marginLeft: '4px', textDecoration: 'none' }}>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        display: 'inline',
                                        color: 'primary.main',
                                        fontWeight: '600',
                                        textDecoration: 'none !important',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {t('login.links.register')}
                                </Typography>
                            </Link>
                            <span>.</span>
                        </Typography>
                        {renderForm}
                    </Card>
                </Stack>
            </Box>
            <ForgotPassword open={forgotPasswordModal} onClose={() => setForgotPasswordModal(false)} setOpen={setOpen} setMessage={setMessage} setSeverity={setSeverity} />
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Login;
